import { useState, CSSProperties, useMemo } from 'react'
import { toast } from 'react-toastify'
import { ToastOptions } from 'react-toastify'
import CreatableSelect from 'react-select/creatable'

import { useHistory } from 'react-router-dom'
import useUser from 'Hooks/useUser'
import useBrand from 'Hooks/useBrand'
import useAppView from 'Hooks/useAppView'
import { PlanType } from 'modules/Const'
import Colors from 'modules/Colors'
import SubscriptionStatusIndicator from './SubscriptionStatusIndicator'
import ModalSymphony from 'elements/ModalSymphony'
import ButtonSymphony from 'elements/ButtonSymphony'
import InputSymphony from 'elements/InputSymphony'
import { toastError } from 'modules/Toast'
import Sty from 'modules/Sty'
import useMaestroChat from 'Hooks/useMaestroChat'
import ContentAnalyzerModal from 'components/ContentAnalyzerModal'
import SwitchSymphony from 'elements/SwitchSymphony'
import TabsSymphony from 'elements/TabsSymphony'
import DatePickerSymphony from 'elements/DatePickerSymphony'
import useAdmin from 'Hooks/useAdmin'
import { updateBrandTierManually_api, extendTrialDaysForBrand } from 'api/adminApi'
import dayjs from 'dayjs'

export const TOAST_OPTIONS: ToastOptions = {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
}

const AdminOptions = () => {
    const { isSymphonyAdmin } = useUser()
    const { currentBrand } = useBrand()
    const { isBrandView } = useAppView()
    const history = useHistory()
    const {
        admin_createTrialSubscription,
        updateBrandTierPreviewLocally,
        isPaidBrand,
        isPreviewingTier,
        getBrand
    } = useBrand()

    const {
        setIsChatOpen
    } = useMaestroChat()

    const {
        showIds,
        setShowIds
    } = useAdmin()


    const isActuallyPaidBrand = isPaidBrand && !isPreviewingTier

    const [showAdminToolsModal, setShowAdminToolsModal] = useState(false)
    const [showContentAnalyzerModal, setShowContentAnalyzerModal] = useState(false)

    const [loading, setLoading] = useState(false)
    const [showTrialDetails, setShowTrialDetails] = useState(false)
    const [showExtendTrialDetails, setShowExtendTrialDetails] = useState(false)
    const [showPreviewDetails, setShowPreviewDetails] = useState(false)
    const [showManualTierUpdate, setShowManualTierUpdate] = useState(false)
    const [selectedPlanType, setSelectedPlanType] = useState<PlanType | null>(PlanType.STARTER)
    const [selectedTierForUpdate, setSelectedTierForUpdate] = useState<'starter' | 'growth'>('starter')
    const [selectedBillingCycle, setSelectedBillingCycle] = useState<'monthly' | 'annual'>('monthly')
    const [trialDays, setTrialDays] = useState(14)
    const [trialDaysToAdd, setTrialDaysToAdd] = useState(7)
    const [selectedTrialTab, setSelectedTrialTab] = useState<'preset' | 'pick-date'>('preset')
    const [selectedTrialEndDate, setSelectedTrialEndDate] = useState<Date | null>(null)

    const [upgradeEmail, setUpgradeEmail] = useState<string>('')
    const [selectedEmailOption, setSelectedEmailOption] = useState<{ label: string; value: string } | null>(null)

    // Create email options from brand users
    const emailOptions = useMemo(() => {
        if (!currentBrand?.users) return []

        return currentBrand.users
            .filter(user => user.email) // Only include users with email addresses
            .map(user => ({
                label: user.name ? `${user.name} (${user.email})` : user.email!,
                value: user.email!
            }))
    }, [currentBrand?.users])

    // Pre-select first user when modal opens and no selection is made
    const handleTrialModalOpen = () => {
        setShowTrialDetails(true)
        if (emailOptions.length > 0 && !selectedEmailOption) {
            const firstOption = emailOptions[0]
            if (firstOption && firstOption.value && firstOption.label) {
                setSelectedEmailOption(firstOption)
                setUpgradeEmail(firstOption.value)
            }
        }
    }

    if (!currentBrand) {
        return null
    }

    const { subscription } = currentBrand

    const updateSubscription = async (planType: PlanType, isTrialFlow: boolean, trialDays?: number) => {
        setLoading(true)
        try {
            if (isTrialFlow) {
                await admin_createTrialSubscription(currentBrand?.id, {
                    planType,
                    trialDays: trialDays ?? undefined,
                    userEmail: upgradeEmail
                })
            } else {
                updateBrandTierPreviewLocally(planType)
            }
            toast.success(`Subscription updated to ${planType} ${!isTrialFlow ? 'until end of session' : ''}.`, TOAST_OPTIONS)
            setShowTrialDetails(false)
            setShowPreviewDetails(false)
        } catch (e) {
            toastError('Error updating subscription')
            console.error(e)
        } finally {
            setLoading(false)
        }
    }

    const handleManualTierUpdate = async () => {
        setLoading(true)
        try {
            const result = await updateBrandTierManually_api({
                brandId: currentBrand?.id,
                tierValue: selectedTierForUpdate,
                billingCycle: selectedBillingCycle
            })

            if (result) {
                toast.success(`Successfully updated brand tier to ${result.newTier} with ${result.billingCycle} billing.`, TOAST_OPTIONS)
                setShowManualTierUpdate(false)
                // Refresh current brand data to show updated tier
                await getBrand()
            } else {
                toastError('Failed to update brand tier')
            }
        } catch (e) {
            toastError('Error updating brand tier')
            console.error(e)
        } finally {
            setLoading(false)
        }
    }

    const handleExtendTrialDays = async () => {
        setLoading(true)
        try {
            let daysToAdd: number

            if (selectedTrialTab === 'preset') {
                daysToAdd = trialDaysToAdd
            } else {
                // Calculate days from current date to selected date
                if (!selectedTrialEndDate) {
                    toastError('Please select a trial end date')
                    setLoading(false)
                    return
                }

                const today = dayjs()
                const endDate = dayjs(selectedTrialEndDate)
                daysToAdd = endDate.diff(today, 'days')

                if (daysToAdd <= 0) {
                    toastError('Trial end date must be in the future')
                    setLoading(false)
                    return
                }

                if (daysToAdd > 365) {
                    toastError('Trial cannot be extended more than 365 days')
                    setLoading(false)
                    return
                }
            }

            await extendTrialDaysForBrand({
                brandId: currentBrand?.id,
                trialDaysToAdd: daysToAdd
            })

            const successMessage = selectedTrialTab === 'preset'
                ? `Successfully added ${daysToAdd} trial days to brand subscription`
                : `Successfully extended trial until ${dayjs(selectedTrialEndDate).format('MMM DD, YYYY')}`

            toast.success(successMessage, TOAST_OPTIONS)
            setShowExtendTrialDetails(false)
            // Refresh current brand data to show updated trial end date
            await getBrand()
        } catch (e) {
            toastError('Error extending trial days')
            console.error(e)
        } finally {
            setLoading(false)
        }
    }

    const handleEmailChange = (selectedOption: { label: string; value: string } | null) => {
        setSelectedEmailOption(selectedOption)
        if (selectedOption) {
            setUpgradeEmail(selectedOption.value)
        } else {
            setUpgradeEmail('')
        }
    }

    const handleExtendTrialModalOpen = () => {
        setShowExtendTrialDetails(true)
        setSelectedTrialTab('preset')
        // Set default date picker value to 7 days from now
        setSelectedTrialEndDate(dayjs().add(7, 'days').toDate())
    }

    const renderDropdown = (showBgColor = false, showFreeOption = true) => {
        return (
            <select
                value={selectedPlanType || currentBrand?.tier || PlanType.FREE}
                onChange={(e) => setSelectedPlanType(e.target.value as PlanType)}
                disabled={loading}
                style={{
                    ...sty.selectInput,
                    backgroundColor: showBgColor ? Colors.purple : '#fff'
                }}
            >
                {showFreeOption &&
                    <option value={PlanType.FREE}>Free</option>
                }
                <option value={PlanType.STARTER}>Starter</option>
                <option value={PlanType.GROWTH}>Growth</option>

            </select>
        )
    }

    const shouldShowTrialButton = () => {
        if (isPreviewingTier) {
            return true
        }
        // Show "Create Trial" button only for free tier brands
        return currentBrand?.tier === PlanType.FREE
    }

    const shouldShowExtendTrialButton = () => {
        if (isPreviewingTier) {
            return false
        }

        if (currentBrand?.tier !== PlanType.FREE && currentBrand?.subscription?.type === 'forced') {
            return false
        }

        // Show "Add Trial Days" button only for paid tier brands
        const paidTiers = [PlanType.STARTER, PlanType.GROWTH, PlanType.LITE, PlanType.PRO]
        return paidTiers.includes(currentBrand?.tier as PlanType)
    }

    const hasStripeCustomer = () => {
        return !!(currentBrand?.subscription?.cid)
    }

    const createSubscriptionClicker = () => {
        const isActiveSubscription = subscription?.status === 'active'
        const isForcedByAdmin = subscription?.details?.forced
        if (subscription && isActiveSubscription && isForcedByAdmin) {
            return (
                <div
                    className="subscriptionStatusContainer"
                    style={{
                        paddingLeft: 16,
                        paddingRight: 16
                    }}
                >
                    <SubscriptionStatusIndicator
                        text="PRO via ADMIN"
                        style={{ marginBottom: 8 }}
                        handleOpenModal={() => {
                            if (subscription.details?.viewSubscriptionUrl) {
                                window.open(subscription.details?.viewSubscriptionUrl!, '_blank')
                            }
                        }}
                    />
                </div>
            )
        }
    }

    const renderButtons = () => {
        return (
            <div className="adminOptionsComponent" style={sty.buttonContainer}>
                {isPreviewingTier &&
                    <ButtonSymphony
                        className="devSubscriptionSpooferPreviewButton"
                        size="small"
                        color="green"
                        onClick={() => setShowAdminToolsModal(true)}
                    >
                        Previewing {currentBrand?.tier.toUpperCase()}
                    </ButtonSymphony>
                }

                <div className="adminToolsButtonContainer" style={{ display: 'flex', gap: 8 }}>
                    <ButtonSymphony
                        className="adminToolsButton"
                        onClick={() => setShowAdminToolsModal(true)}
                        variant="outlined"
                        color="white"
                        size="small"
                        width="100%"
                    >
                        Admin Tools
                    </ButtonSymphony>
                    <SwitchSymphony
                        className="adminToolsSwitch"
                        checked={showIds}
                        onChange={(value) => setShowIds(value)}
                        color="green"
                    />
                </div>
            </div>
        )
    }

    return (
        <>
            {createSubscriptionClicker()}
            {isSymphonyAdmin &&
                <div
                    className="adminOptionsContainer flex flex-row items-center mr-auto w-full justify-start"
                    style={{
                        paddingLeft: 16,
                        paddingRight: 16
                    }}
                >
                    {renderButtons()}
                </div>
            }


            <ModalSymphony
                className='adminToolsOptions'
                isOpen={showAdminToolsModal}
                title="Admin Tools"
                onClose={() => setShowAdminToolsModal(false)}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
                    <div className='adminToolsOptions_onboarding' style={sty.adminToolsOptionsContent}>
                        <p style={{ ...Sty.text18bold }}>Onboarding & Demos:</p>
                        <div className="buttonWrapperContainer" style={sty.buttonWrapperContainer}>
                            <ButtonSymphony
                                className="adminTeamOnboardingButton"
                                onClick={() => {
                                    history.push('/admin/onboarding')
                                    setShowAdminToolsModal(false)
                                }}
                            >
                                Admin: Onboard a Team
                            </ButtonSymphony>


                            {shouldShowTrialButton() &&
                                <ButtonSymphony
                                    className="devSubscriptionSpooferTrialButton"
                                    color="purpleLight"
                                    onClick={handleTrialModalOpen}
                                >
                                    Admin: Create Paid Plan Trial
                                </ButtonSymphony>
                            }

                            {shouldShowExtendTrialButton() &&
                                <ButtonSymphony
                                    className="extendTrialDaysButton"
                                    color="purpleLight"
                                    onClick={handleExtendTrialModalOpen}
                                >
                                    Admin: Add Free Trial Days
                                </ButtonSymphony>
                            }

                            {hasStripeCustomer() &&
                                <ButtonSymphony
                                    className="viewStripeCustomerButton"
                                    variant="outlined"
                                    color="green"
                                    onClick={() => {
                                        const stripeCustomerId = currentBrand?.subscription?.cid
                                        if (stripeCustomerId) {
                                            const stripeCustomerUrl = `https://dashboard.stripe.com/customers/${stripeCustomerId}`
                                            window.open(stripeCustomerUrl, '_blank')
                                        }
                                    }}
                                >
                                    View Stripe Customer
                                </ButtonSymphony>
                            }

                            <ButtonSymphony
                                className="devSubscriptionSpooferPreviewButton"
                                variant="outlined"
                                onClick={() => setShowPreviewDetails(true)}
                            >
                                {isPreviewingTier ?
                                    `Edit Preview Tier (Currently previewing ${currentBrand?.tier.toUpperCase()})`
                                    :
                                    'Preview as Different Tier'
                                }
                            </ButtonSymphony>

                            <ButtonSymphony
                                className="manualTierUpdateButton"
                                color="purpleLight"
                                onClick={() => setShowManualTierUpdate(true)}
                            >
                                Update Subscription Tier Manually
                            </ButtonSymphony>
                        </div>
                    </div>
                    <div className='adminToolsOptions_cs' style={sty.adminToolsOptionsContent}>
                        <div style={Sty.text18bold}>CS Team:</div>
                        <div className="buttonWrapperContainer" style={sty.buttonWrapperContainer}>
                            <ButtonSymphony
                                className='brandEditingToolsButton'
                                onClick={() => {
                                    history.push('/admintools')
                                    setShowAdminToolsModal(false)
                                }}
                                color="green"
                            >
                                Brand Editing Tools
                            </ButtonSymphony>
                            <ButtonSymphony
                                className='brandEditingToolsButton'
                                onClick={() => {
                                    history.push('/admin/video-views-data-upload')
                                    setShowAdminToolsModal(false)
                                }}
                                color="green"
                            >
                                Video Views Data Upload
                            </ButtonSymphony>
                            <ButtonSymphony
                                className='agentCampaignsButton'
                                onClick={() => {
                                    history.push('/admin/agent-campaigns')
                                    setShowAdminToolsModal(false)
                                }}
                                color="green"
                            >
                                Admin Campaigns
                            </ButtonSymphony>
                            <ButtonSymphony
                                className='stripeSyncButton'
                                onClick={() => {
                                    history.push('/admin/stripe-sync')
                                    setShowAdminToolsModal(false)
                                }}
                                color="green"
                            >
                                Stripe Subscription Sync
                            </ButtonSymphony>
                            <ButtonSymphony
                                className='invoiceDownloaderButton'
                                onClick={() => {
                                    history.push('/admin/invoice-downloader')
                                    setShowAdminToolsModal(false)
                                }}
                                color="green"
                            >
                                Invoice Downloader
                            </ButtonSymphony>
                            {isActuallyPaidBrand &&
                                <ButtonSymphony
                                    className="viewBillingInfoButton"
                                    variant="outlined"
                                    color="green"
                                    onClick={() => {
                                        history.push('/settings?tab=BILLING')
                                        setShowAdminToolsModal(false)
                                    }}
                                >
                                    View Billing Info
                                </ButtonSymphony>
                            }
                        </div>
                    </div>
                    <div className='partnershipTools' style={sty.adminToolsOptionsContent}>
                        <p style={{ ...Sty.text18bold }}>Partnership Team:</p>
                        <ButtonSymphony
                            className="detailsAdminOptions"
                            variant="outlined"
                            color="green"
                            onClick={() => {
                                history.push('/admin/affiliates')
                                setShowAdminToolsModal(false)
                            }}
                        >
                            View Affiliate Data
                        </ButtonSymphony>
                    </div>

                    <div className='adminToolsOptions_dev' style={sty.adminToolsOptionsContent}>
                        <p style={{ ...Sty.text18bold }}>Dev Team:</p>
                        <div className="buttonWrapperContainer" style={sty.buttonWrapperContainer}>
                            <ButtonSymphony
                                className="allWebsitesButton"
                                onClick={() => {
                                    history.push('/allwebsites')
                                    setShowAdminToolsModal(false)
                                }}
                                variant="outlined"
                                color="grey"
                            >
                                All Websites
                            </ButtonSymphony>
                            <ButtonSymphony
                                className="elementsPageButton"
                                onClick={() => {
                                    history.push('/elementspage')
                                    setShowAdminToolsModal(false)
                                }}
                                variant="outlined"
                                color="grey"
                            >
                                Elements Page
                            </ButtonSymphony>
                            <ButtonSymphony
                                className="instagramContentAnalyzerButton"
                                onClick={() => {
                                    setShowContentAnalyzerModal(true)
                                    setShowAdminToolsModal(false)
                                }}
                                variant="outlined"
                                color="grey"
                            >
                                Instagram Content Analyzer
                            </ButtonSymphony>
                            <ButtonSymphony
                                className="maestroChatButton"
                                onClick={() => {
                                    setIsChatOpen(true)
                                }}
                                variant="outlined"
                            >
                                Open Maestro Chat
                            </ButtonSymphony>
                            <ButtonSymphony
                                className="testApiButton"
                                onClick={() => {
                                    history.push('/testapi')
                                    setShowAdminToolsModal(false)
                                }}
                                variant="outlined"
                                color="grey"
                            >
                                Test API
                            </ButtonSymphony>
                            <ButtonSymphony
                                className="testApiButton"
                                onClick={() => {
                                    history.push('/api-keys')
                                    setShowAdminToolsModal(false)
                                }}
                                variant="outlined"
                                color="grey"
                            >
                                API Keys
                            </ButtonSymphony>
                            <ButtonSymphony
                                className="testApiButton"
                                onClick={() => {
                                    history.push('/campaigns-dashboard')
                                    setShowAdminToolsModal(false)
                                }}
                                variant="outlined"
                                color="grey"
                            >
                                Campaigns Dashboard
                            </ButtonSymphony>
                        </div>
                    </div>
                </div>
            </ModalSymphony>

            <ModalSymphony
                className="devSubscriptionSpooferModal"
                size="small"
                title={showTrialDetails ? 'Admin: Set Trial Duration' : 'Admin: Preview as a Different Plan'}
                subtitle={showTrialDetails ?
                    'This will create a subscription trial for the brand. The profile will be asked to enter card to continue the trial after the trial period.'
                    :
                    'This will temporarily change the subscription plan for testing purposes. No billing changes will be made.'
                }
                isOpen={showTrialDetails || showPreviewDetails}
                onClose={() => {
                    setShowTrialDetails(false)
                    setShowPreviewDetails(false)
                    setSelectedEmailOption(null)
                    setUpgradeEmail('')
                }}
            >
                <div style={sty.modalContent}>
                    <div style={sty.inputContainer}>
                        <p>Plan</p>
                        {renderDropdown(false, !showTrialDetails)}
                    </div>
                    {showTrialDetails &&
                        <>
                            <div style={sty.inputContainer}>
                                <p>Trial Duration</p>
                                <select
                                    value={trialDays}
                                    onChange={(e) => setTrialDays(Number(e.target.value))}
                                    disabled={loading}
                                    style={sty.selectInput}
                                >
                                    <option value={7}>7 Days</option>
                                    <option value={14}>14 Days</option>
                                    <option value={30}>30 Days</option>
                                    <option value={60}>60 Days</option>
                                    <option value={90}>90 Days</option>
                                </select>
                            </div>
                            <div style={sty.inputContainer}>
                                <p>User Email</p>
                                <CreatableSelect
                                    className="devSubscriptionSpooferTrialEmailInput"
                                    placeholder="Select or enter user email"
                                    isClearable
                                    isDisabled={loading}
                                    options={emailOptions}
                                    value={selectedEmailOption}
                                    onChange={handleEmailChange}
                                    onCreateOption={(inputValue: string) => {
                                        const newOption = { label: inputValue, value: inputValue }
                                        setSelectedEmailOption(newOption)
                                        setUpgradeEmail(inputValue)
                                    }}
                                    formatCreateLabel={(inputValue: string) => `Create "${inputValue}"`}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '40px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                        }),
                                        placeholder: (base) => ({
                                            ...base,
                                            color: '#999',
                                        }),
                                    }}
                                />
                            </div>
                        </>
                    }
                </div>
                <ButtonSymphony
                    className="devSubscriptionSpooferConfirmButton"
                    isLoading={loading}
                    onClick={() => selectedPlanType && updateSubscription(selectedPlanType, showTrialDetails, trialDays)}
                    variant="outlined"
                    disabled={loading || (!upgradeEmail && showTrialDetails) || (showTrialDetails && !selectedPlanType)}
                >
                    {loading ? 'Updating...' : 'Confirm'}
                </ButtonSymphony>
            </ModalSymphony>

            <ModalSymphony
                className="extendTrialDaysModal"
                size="small"
                title="Admin: Add Free Trial Days"
                subtitle="This will extend the current subscription's trial period."
                isOpen={showExtendTrialDetails}
                onClose={() => {
                    setShowExtendTrialDetails(false)
                    setSelectedTrialTab('preset')
                    setSelectedTrialEndDate(null)
                }}
            >
                <div style={sty.modalContent}>
                    <div style={sty.tabContainer}>
                        <TabsSymphony
                            className="trialExtensionTabs"
                            tabs={[
                                { id: 'preset', title: 'Preset' },
                                { id: 'pick-date', title: 'Pick a Date' }
                            ]}
                            selectedTab={selectedTrialTab}
                            onChange={(tab) => {
                                const tabId = typeof tab === 'string' ? tab : tab.id
                                setSelectedTrialTab(tabId as 'preset' | 'pick-date')
                            }}
                        />
                    </div>

                    {selectedTrialTab === 'preset' ? (
                        <div style={sty.inputContainer}>
                            <p>Trial Days to Add</p>
                            <select
                                value={trialDaysToAdd}
                                onChange={(e) => setTrialDaysToAdd(Number(e.target.value))}
                                disabled={loading}
                                style={sty.selectInput}
                            >
                                <option value={7}>7 Days</option>
                                <option value={14}>14 Days</option>
                                <option value={30}>30 Days</option>
                                <option value={60}>60 Days</option>
                            </select>
                        </div>
                    ) : (
                        <div style={sty.inputContainer}>
                            <p>Trial End Date</p>
                            <DatePickerSymphony
                                className="trialEndDatePicker"
                                selected={selectedTrialEndDate}
                                onChange={(date) => setSelectedTrialEndDate(date)}
                                minDate={dayjs().add(1, 'day').toDate()}
                                maxDate={dayjs().add(365, 'days').toDate()}
                                placeholderText="Select trial end date"
                                disabled={loading}
                                dateFormat="MM/dd/yyyy"
                            />
                        </div>
                    )}
                </div>
                <ButtonSymphony
                    className="extendTrialDaysConfirmButton"
                    isLoading={loading}
                    onClick={handleExtendTrialDays}
                    color="purpleLight"
                    disabled={loading || (selectedTrialTab === 'pick-date' && !selectedTrialEndDate)}
                >
                    {loading ? 'Extending...' : 'Confirm'}
                </ButtonSymphony>
            </ModalSymphony>

            <ModalSymphony
                className="manualTierUpdateModal"
                size="small"
                title="Admin: Update Subscription Tier Manually"
                subtitle="This will permanently update the brand's subscription tier and billing cycle in the database."
                isOpen={showManualTierUpdate}
                onClose={() => setShowManualTierUpdate(false)}
            >
                <div style={sty.modalContent}>
                    <div style={sty.inputContainer}>
                        <p>Tier</p>
                        <select
                            value={selectedTierForUpdate}
                            onChange={(e) => setSelectedTierForUpdate(e.target.value as 'starter' | 'growth')}
                            disabled={loading}
                            style={sty.selectInput}
                        >
                            <option value="starter">Starter</option>
                            <option value="growth">Growth</option>
                        </select>
                    </div>
                    <div style={sty.inputContainer}>
                        <p>Billing Cycle</p>
                        <select
                            value={selectedBillingCycle}
                            onChange={(e) => setSelectedBillingCycle(e.target.value as 'monthly' | 'annual')}
                            disabled={loading}
                            style={sty.selectInput}
                        >
                            <option value="monthly">Monthly</option>
                            <option value="annual">Annual</option>
                        </select>
                    </div>
                </div>
                <ButtonSymphony
                    className="manualTierUpdateConfirmButton"
                    isLoading={loading}
                    onClick={handleManualTierUpdate}
                    color="purpleLight"
                    disabled={loading}
                >
                    {loading ? 'Updating...' : 'Update Tier'}
                </ButtonSymphony>
            </ModalSymphony>

            <ContentAnalyzerModal
                isOpen={showContentAnalyzerModal}
                onClose={() => setShowContentAnalyzerModal(false)}
            />
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    buttonContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 8
    },
    modalContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 12,
        width: '100%'
    },
    inputContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 4
    },
    formControl: {
        padding: '0 8px'
    },
    selectInput: {
        width: '100%',
        padding: '8px 12px',
        fontSize: '14px',
        borderRadius: '4px',
        border: '1px solid #ccc',
        backgroundColor: '#fff',
        outline: 'none',
        cursor: 'pointer'
    },
    adminToolsOptionsContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8
    },
    buttonWrapperContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8
    },
    tabContainer: {
        width: '100%',
        marginBottom: 16
    }
}

export default AdminOptions